import type { AccountListItem } from '../api-client';
import { createTransfer } from '../api-client';
import { parseAmountInput } from '@lib/amount-parse';
import { todayLocalIsoDate } from '@lib/date-utils';
import { renderAccountPicker } from '../pages/account-picker';
import { showToast } from './toast';

export interface PayCardSheetProps {
  card: { id: string; name: string; current_balance_aed: number };
  defaultFundingAccountId: string | null;
  accounts: ReadonlyArray<AccountListItem>;
  onClose(): void;
  onSuccess(): void;
}

export function openPayCardSheet(root: HTMLElement, props: PayCardSheetProps): void {
  let fundingId: string | null = props.defaultFundingAccountId;

  const render = () => {
    const funding = fundingId ? props.accounts.find((a) => a.id === fundingId) : null;
    const fundingLabel = funding ? funding.name : 'Choose account';
    const disabled = !fundingId ? 'disabled' : '';

    root.innerHTML = `
      <div class="sheet-backdrop" id="sheet-backdrop">
        <div class="sheet pay-card-sheet" role="dialog" aria-modal="true">
          <header class="sheet-header">
            <h3>Pay this card</h3>
            <div class="sheet-card-name">${escapeHtml(props.card.name)}</div>
          </header>
          <div class="sheet-body">
            <label class="sheet-field">
              <span class="sheet-label">Amount</span>
              <input id="pay-amount" type="text" inputmode="decimal" value="${props.card.current_balance_aed.toFixed(2)}" />
              <small class="sheet-helper">Current ledger balance. Edit if your statement/payment amount differs.</small>
            </label>
            <button class="sheet-row" id="pay-funding-row">
              <span class="sheet-label">From</span>
              <span class="sheet-value">${escapeHtml(fundingLabel)}</span>
              <span class="sheet-chevron">›</span>
            </button>
            <label class="sheet-field">
              <span class="sheet-label">Date</span>
              <input id="pay-date" type="date" value="${todayLocalIsoDate()}" max="${todayLocalIsoDate()}" />
            </label>
            <div class="sheet-error" id="pay-error" hidden></div>
          </div>
          <footer class="sheet-footer">
            <button class="ghost" id="pay-cancel">Cancel</button>
            <button class="primary" id="pay-confirm" ${disabled}>Record payment</button>
          </footer>
        </div>
      </div>
    `;

    const close = () => {
      root.innerHTML = '';
      props.onClose();
    };

    root.querySelector<HTMLElement>('#sheet-backdrop')!.addEventListener('click', (e) => {
      if (e.target === e.currentTarget) close();
    });
    root.querySelector<HTMLButtonElement>('#pay-cancel')!.addEventListener('click', close);

    root.querySelector<HTMLButtonElement>('#pay-funding-row')!.addEventListener('click', () => {
      const pickerMount = document.createElement('div');
      root.appendChild(pickerMount);
      renderAccountPicker(pickerMount, [...props.accounts], {
        onPick: (id) => {
          fundingId = id;
          pickerMount.remove();
          render();
        },
        onCancel: () => pickerMount.remove(),
      }, { filter: 'asset_spendable', selectedId: fundingId });
    });

    root.querySelector<HTMLButtonElement>('#pay-confirm')!.addEventListener('click', async () => {
      const errorEl = root.querySelector<HTMLElement>('#pay-error')!;
      errorEl.hidden = true;
      errorEl.textContent = '';

      const amountRaw = root.querySelector<HTMLInputElement>('#pay-amount')!.value;
      const dateValue = root.querySelector<HTMLInputElement>('#pay-date')!.value;
      const parsed = parseAmountInput(amountRaw);
      if (!parsed.ok) {
        errorEl.textContent = describeParseError(parsed.code);
        errorEl.hidden = false;
        return;
      }
      if (!fundingId) {
        errorEl.textContent = 'Pick a funding account.';
        errorEl.hidden = false;
        return;
      }
      const fundingAccount = props.accounts.find((a) => a.id === fundingId)!;
      try {
        await createTransfer({
          from_account_id: fundingId,
          to_account_id: props.card.id,
          amount: parsed.amount,
          currency: parsed.currency,
          occurred_at: dateValue,
          description: `Card payment: ${props.card.name} from ${fundingAccount.name}`,
        });
        showToast(`Payment recorded to ${props.card.name}`);
        root.innerHTML = '';
        props.onSuccess();
      } catch (e) {
        errorEl.textContent = e instanceof Error ? e.message : String(e);
        errorEl.hidden = false;
      }
    });
  };

  render();
}

function describeParseError(code: 'empty' | 'no_amount' | 'unknown_currency' | 'non_positive'): string {
  switch (code) {
    case 'empty':            return 'Enter an amount.';
    case 'no_amount':        return 'Enter a valid number.';
    case 'unknown_currency': return 'Unknown currency code. Use AED, USD, GBP, EUR, or CHF.';
    case 'non_positive':     return 'Amount must be greater than zero.';
  }
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
